// src/pages/AccountOrderDetailPage.tsx
// Detailed view of a single past order with a star-rating review form.

import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Order } from "square";
import { getOrder } from "../api";
import { submitOrderReview } from "../api/customer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import PageContainer, { GapSize } from "../components/PageContainer";
import OrderWrapper from "../components/OrderWrapper";
import OrderTimeline from "../components/OrderTimeline";
import Spinner from "../components/Spinner";
import CopyButton from "../components/CopyButton";
import Tooltip from "../components/Tooltip";
import {
  fulfillmentLabel,
  fulfillmentBadgeClass,
  fulfillmentDescription,
  hasShipped,
} from "../utils/fulfillmentStatus";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const formatMoney = (amount?: number | bigint | null): string => {
  if (amount == null) return "—";
  return `$${(Number(amount) / 100).toFixed(2)}`;
};

// ---------------------------------------------------------------------------
// Star Rating component
// ---------------------------------------------------------------------------

interface StarRatingProps {
  value: number;
  onChange?: (rating: number) => void;
  readOnly?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  readOnly,
}) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(star)}
          onMouseEnter={() => !readOnly && setHovered(star)}
          onMouseLeave={() => !readOnly && setHovered(0)}
          className={`text-3xl transition-colors ${readOnly ? "cursor-default" : "cursor-pointer"} ${
            star <= (hovered || value) ? "text-yellow-500" : "text-gray-300"
          }`}
          aria-label={`${star} star${star > 1 ? "s" : ""}`}
        >
          ★
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review Section
// ---------------------------------------------------------------------------

interface ReviewSectionProps {
  order: Order;
  authToken: string;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ order, authToken }) => {
  const metadata = (order as any).metadata ?? {};
  const existingRating = metadata.review_rating
    ? Number(metadata.review_rating)
    : 0;
  const existingText = metadata.review_text ?? "";
  const existingDate = metadata.review_date ?? "";
  const existingAuthor = metadata.review_author ?? "";

  const [rating, setRating] = useState(existingRating);
  const [reviewText, setReviewText] = useState(existingText);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(!!existingRating);
  const [isEditing, setIsEditing] = useState(false);

  // "Shipped" is a FULFILLMENT state, not an order state: printful-webhook flips
  // the SHIPMENT fulfillment to COMPLETED and intentionally leaves order.state
  // OPEN. Gating on order.state === "COMPLETED" therefore never came true for a
  // normally-shipped order — while request-reviews.mjs selects its recipients by
  // fulfillment COMPLETED, so every review-request email landed on a page saying
  // to wait for delivery. Gate on the same signal the email uses.
  //
  // The waiting copy below still says "delivered" while this gate opens at
  // shipped — that mismatch is deliberate, not a leftover. Delivery is the
  // moment we WANT a review written (nobody can judge a shirt they haven't
  // worn), but we get no delivery signal from Printful, so shipped is the
  // latest event we can gate on. Opening early is harmless: the review request
  // only goes out REVIEW_REQUEST_DELAY_DAYS (7) after shipping, by which point
  // the package has landed. Don't "fix" the copy to match the gate.
  const canReview = hasShipped(order) && order.state !== "CANCELED";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating < 1) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const result = await submitOrderReview(
        authToken,
        order.id!,
        rating,
        reviewText,
      );
      if (!result || result.error) {
        setSubmitError(result?.error ?? "Failed to submit review.");
        return;
      }
      setSubmitted(true);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canReview) {
    return (
      <div className="text-sm text-gray-500 lora-text italic">
        Reviews are available once your order has been delivered.
      </div>
    );
  }

  if (submitted && !isEditing) {
    return (
      <div className="flex flex-col gap-2">
        <StarRating value={rating} readOnly />
        {reviewText && (
          <p className="text-sm text-gray-700 lora-text italic">
            &ldquo;{reviewText}&rdquo;
          </p>
        )}
        <div className="flex items-center gap-3 text-xs text-gray-400">
          {existingDate && <span>{formatDate(existingDate)}</span>}
          {existingAuthor && <span>by {existingAuthor}</span>}
          <button
            onClick={() => setIsEditing(true)}
            className="underline text-brown hover:text-brown-dark"
          >
            Edit review
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <StarRating value={rating} onChange={setRating} />
      {rating > 0 && (
        <p className="text-sm text-gray-500">
          {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
        </p>
      )}
      <textarea
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        placeholder="Share your experience (optional)…"
        maxLength={1000}
        rows={3}
        className="order-input-field resize-none"
      />
      {submitError && <p className="text-rust text-sm">{submitError}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting || rating < 1}
          className="btn-primary text-sm px-4 py-2"
        >
          {isSubmitting
            ? "Submitting…"
            : submitted
              ? "Update Review"
              : "Submit Review"}
        </button>
        {isEditing && (
          <button
            type="button"
            onClick={() => {
              setIsEditing(false);
              setRating(existingRating);
              setReviewText(existingText);
            }}
            className="btn-secondary text-sm px-4 py-2"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const AccountOrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { session, isLoggedIn } = useCustomerAuth();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect to login if not authenticated — carrying this order's path so
  // that after the OTP the customer lands back HERE (the review-request email
  // links straight to this page for logged-out recipients).
  useEffect(() => {
    if (!isLoggedIn)
      navigate(
        `/account/login?returnTo=${encodeURIComponent(`/account/orders/${orderId ?? ""}`)}`,
      );
  }, [isLoggedIn, navigate, orderId]);

  useEffect(() => {
    if (!orderId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const result = await getOrder(orderId, {
          authToken: session?.authToken,
        });
        if (result?.data) {
          setOrder(result.data);
        } else {
          setError("Order not found.");
        }
      } catch (err) {
        setError("Failed to load order.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (!isLoggedIn || !session) return null;

  const fulfillmentState = order?.fulfillments?.[0]?.state;
  const shipmentDetails = order?.fulfillments?.[0]?.shipmentDetails;
  const shipped = hasShipped(order ?? undefined);

  return (
    <>
      {isLoading && <Spinner text="Loading Order..." />}
      <PageContainer hasPadding gapSize={GapSize.Small}>
        <div className="max-w-4xl mx-auto w-full flex flex-col gap-4">
          {/* Back Link */}
          <Link
            to="/account"
            className="text-sm text-brown hover:text-brown-dark underline opensans-text self-start"
          >
            ← Back to My Account
          </Link>

          {error && (
            <OrderWrapper>
              <p className="text-rust text-center">{error}</p>
            </OrderWrapper>
          )}

          {order && (
            <>
              {/* Order Header */}
              <OrderWrapper>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <h1 className="text-2xl playfair-text text-brown-dark">
                      Order Details
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap">
                      <CopyButton textToCopy={order.id ?? ""} />
                      <span
                        className="text-xs text-gray-400 font-mono"
                        translate="no"
                      >
                        {order.id}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Placed {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-col items-start sm:items-end gap-2">
                    {fulfillmentState && (
                      <Tooltip
                        tooltip={fulfillmentDescription(fulfillmentState)}
                        position="bottom"
                        multiline
                      >
                        <span
                          className={`${fulfillmentBadgeClass(
                            fulfillmentState,
                          )} cursor-help`}
                        >
                          {fulfillmentLabel(fulfillmentState)}
                        </span>
                      </Tooltip>
                    )}
                    <Link
                      to={`/orders/track?id=${order.id}`}
                      className="btn-secondary text-xs px-3 py-1"
                    >
                      Track Shipment
                    </Link>
                  </div>
                </div>

                {/* Status progress timeline */}
                <div className="w-full h-px bg-accent-light mt-4" />
                <div className="pt-4">
                  <OrderTimeline
                    fulfillmentState={fulfillmentState}
                    orderState={order.state ?? undefined}
                  />
                </div>
              </OrderWrapper>

              {/* Line Items */}
              {order.lineItems && order.lineItems.length > 0 && (
                <OrderWrapper>
                  <h2 className="text-lg playfair-text text-brown-dark">
                    Items Ordered
                  </h2>
                  <div className="w-full h-px bg-accent-light" />
                  <ul className="flex flex-col gap-2">
                    {order.lineItems.map((item, i) => (
                      <li
                        key={i}
                        className="flex justify-between items-center text-sm py-1 border-b border-accent-light last:border-none"
                      >
                        <span className="text-gray-800">
                          {item.name}{" "}
                          <span className="text-gray-400">
                            × {item.quantity}
                          </span>
                        </span>
                        <span className="font-semibold text-brown-dark">
                          {formatMoney(item.basePriceMoney?.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Totals */}
                  <div className="pt-2 border-t border-accent flex flex-col gap-1">
                    {order.serviceCharges?.map((sc, i) => (
                      <div
                        key={i}
                        className="flex justify-between text-sm text-gray-600"
                      >
                        <span>{sc.name}</span>
                        <span>{formatMoney(sc.amountMoney?.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold text-brown-dark mt-1">
                      <span>Total</span>
                      <span>{formatMoney(order.totalMoney?.amount)}</span>
                    </div>
                  </div>
                </OrderWrapper>
              )}

              {/* Shipping Info */}
              {shipmentDetails && (
                <OrderWrapper>
                  <h2 className="text-lg playfair-text text-brown-dark">
                    Shipping
                  </h2>
                  <div className="w-full h-px bg-accent-light" />
                  <div className="text-sm text-gray-700 flex flex-col gap-1">
                    {shipmentDetails.recipient?.displayName && (
                      <p className="font-semibold">
                        {shipmentDetails.recipient.displayName}
                      </p>
                    )}
                    {shipmentDetails.recipient?.address && (
                      <>
                        <p>{shipmentDetails.recipient.address.addressLine1}</p>
                        {shipmentDetails.recipient.address.addressLine2 && (
                          <p>
                            {shipmentDetails.recipient.address.addressLine2}
                          </p>
                        )}
                        <p>
                          {shipmentDetails.recipient.address.locality},{" "}
                          {
                            shipmentDetails.recipient.address
                              .administrativeDistrictLevel1
                          }{" "}
                          {shipmentDetails.recipient.address.postalCode}
                        </p>
                      </>
                    )}
                    {shipmentDetails.trackingNumber && (
                      <p className="mt-2">
                        <span className="font-semibold">Tracking: </span>
                        <span className="font-mono text-xs" translate="no">
                          {shipmentDetails.trackingNumber}
                        </span>
                      </p>
                    )}
                    {shipmentDetails.trackingUrl && (
                      <a
                        href={shipmentDetails.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brown underline hover:text-brown-dark text-xs"
                      >
                        Track Package →
                      </a>
                    )}
                  </div>
                </OrderWrapper>
              )}

              {/* Review Section */}
              <OrderWrapper>
                <h2 className="text-lg playfair-text text-brown-dark">
                  Leave a Review
                </h2>
                <div className="w-full h-px bg-accent-light" />
                <ReviewSection order={order} authToken={session.authToken} />
              </OrderWrapper>

              {/* Actions */}
              {order.state !== "CANCELED" && (
                <OrderWrapper>
                  <h2 className="text-lg playfair-text text-brown-dark">
                    Need Help?
                  </h2>
                  <div className="w-full h-px bg-accent-light" />
                  <div className="flex flex-wrap gap-3">
                    {/* Same fulfillment-vs-order-state trap as the review gate
                        above: a shipped order stays order.state OPEN, so this
                        offered Cancel (impossible — it's already out the door)
                        and withheld Start Return. */}
                    {!shipped && (
                      <Link
                        to={`/orders/cancel?id=${order.id}`}
                        className="btn-secondary text-sm"
                      >
                        Cancel Order
                      </Link>
                    )}
                    {shipped && (
                      <Link
                        to={`/orders/return?id=${order.id}`}
                        className="btn-secondary text-sm"
                      >
                        Start Return
                      </Link>
                    )}
                    <a
                      href="mailto:support@americanaandcompany.com"
                      className="btn-secondary text-sm"
                    >
                      Contact Support
                    </a>
                  </div>
                </OrderWrapper>
              )}
            </>
          )}
        </div>
      </PageContainer>
    </>
  );
};

export default AccountOrderDetailPage;
